import {
  BMComponentsType,
  BMInputField,
  BMPrimitives,
  BMText,
  useBMTheme,
} from "@traveloka/district-bloom";
import { memo, useCallback, useMemo, useState } from "react";
import { StyleSheet } from "react-native";

import { Controller, useFormContext } from "react-hook-form";
import BMView from "../../../../components/View/BMView";
import { useMobileContentResource } from "../../../../core/resource/MobileResourceProvider";
import { formatMessage } from "../../../../utils";
import { MobileWebDocumentField } from "../../common";

/**
 * Type definition for additional info form value
 * @typedef {Object} AdditionalInfoFormValue
 * @property {Object|null|undefined} attachment - The uploaded attachment file and its URL, undefined if not provided (not required)
 * @property {File} attachment.file - The uploaded file
 * @property {string} attachment.url - The URL of the uploaded file
 * @property {string|null|undefined} notes - Additional notes provided by the user,undefined if not provided (not required)
 */
type AdditionalInfoFormValue = {
  attachment:
    | {
        file: File;
        url: string;
      }
    | null
    | undefined;
  notes: string | null | undefined;
};

type Props = {
  onUploadFile?: (file: File) => Promise<string>;
};

const DOCUMENT_EXTENSIONS = [".jpg", ".jpeg", ".png", ".pdf"] as const;
const DOCUMENT_SIZE_IN_MB = "20";

/**
 * Flight Prebook Additional Information Component
 * @todo
 * Create this as a generic component to be used in all products (not only flight)
 * change the name to ProductPrebookAdditionalInfo
 *
 * @component
 * Renders UI for collecting additional information during a prebook flow.
 * This includes an optional attachment upload (image/pdf) and an optional notes field.
 *
 * IMPORTANT: This component must be rendered inside a react-hook-form FormProvider (or have
 * react-hook-form context available via useFormContext). The form's generic/type must match
 * the following shape:
 *
 * type AdditionalInfoFormValue = {
 *   attachment?: { file: File; url: string } | null | undefined;
 *   notes?: string | null | undefined;
 * }
 *
 * with both fields being optional (undefined).
 */
const FlightPrebookAdditionalInfo = memo(
  function FlightPrebookAdditionalInformationBase({ onUploadFile }: Props) {
    const [loading, setLoading] = useState(false);

    const { BMComponents } = useBMTheme();
    const styles = useMemo(() => createStyles(BMComponents), [BMComponents]);
    const { CorporateProductPrebookAdditionalInfo: cr } =
      useMobileContentResource();

    const { control, getValues, setValue, setError, clearErrors } =
      useFormContext<AdditionalInfoFormValue>();

    // Check if any of the fields is required
    const isAnyRequired = useMemo(() => {
      return (
        getValues("attachment") !== undefined &&
        getValues("notes") !== undefined
      );
    }, [getValues]);

    const caption = useMemo(
      () =>
        formatMessage(cr.attachmentFileExtensionSizeTextHelper, {
          size: DOCUMENT_SIZE_IN_MB,
          extension: DOCUMENT_EXTENSIONS.join("/"),
        }),
      [cr.attachmentFileExtensionSizeTextHelper]
    );

    const handleOnChangeFile = useCallback(
      (file: File | undefined) => {
        // If no file, set attachment to null
        if (!file) {
          setValue("attachment", null);
          return;
        }

        try {
          validate(file);
        } catch {
          setError("attachment", { type: "manual" });
          return;
        }

        // Upload file and get URL
        setLoading(true);
        onUploadFile?.(file)
          .then((url) => {
            setValue("attachment", { file, url });
          })
          .catch(() => {
            setValue("attachment", null);
          })
          .finally(() => {
            clearErrors("attachment");
            setLoading(false);
          });
      },
      [onUploadFile, setValue]
    );

    return (
      <BMView spacing="md">
        <BMView spacing="xs">
          <BMText variant="uiHuge">
            {formatMessage(cr.title, { optional: String(!isAnyRequired) })}
          </BMText>
          <BMText variant="uiSmall" ink="BMStyleTextInkSecondary">
            {cr.subtitle}
          </BMText>
        </BMView>
        <BMView style={styles.card} spacing="lg">
          <Controller
            name="attachment"
            control={control}
            render={({ field: { value }, fieldState: { error } }) => (
              <MobileWebDocumentField
                caption={caption}
                label={cr.attachmentFileTitle}
                isAPISuccess={Boolean(value?.url)}
                errorMessage={
                  error
                    ? formatMessage(cr.attachmentFileExtensionSizeTextHelper, {
                        size: DOCUMENT_SIZE_IN_MB,
                        extension: DOCUMENT_EXTENSIONS.join("/"),
                      })
                    : undefined
                }
                isLoading={loading}
                onChange={handleOnChangeFile}
                placeholder={cr.attachmentFilePlaceholder}
              />
            )}
          />
          <Controller
            name="notes"
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => {
              return (
                <BMInputField
                  variant="AREA"
                  value={value ?? ""}
                  error={error?.message}
                  label={cr.bookingNotesTitle}
                  placeholder={cr.bookingNotesPlaceholder}
                  onChangeText={(v) => onChange(v.length ? v : undefined)}
                  height={92}
                />
              );
            }}
          />
        </BMView>
      </BMView>
    );
  }
);

export default FlightPrebookAdditionalInfo;

function createStyles(BMComponents: BMComponentsType) {
  return StyleSheet.create({
    card: {
      padding: BMPrimitives.BMSize.md,
      borderRadius: BMPrimitives.BMRadius.md,
      borderWidth: BMPrimitives.BMBorder.width.thick,
      borderColor: BMComponents.BMStyleLineBordercolorSubtle,
    },
  });
}

function validate(file: File) {
  const fileName = file.name ?? "";
  const lowerName = fileName.toLowerCase();

  const isValidExtension = DOCUMENT_EXTENSIONS.some((ext) =>
    lowerName.endsWith(ext)
  );
  if (!isValidExtension) {
    throw new Error("INVALID_EXTENSION");
  }

  const maxBytes = parseInt(DOCUMENT_SIZE_IN_MB, 10) * 1024 * 1024;
  if (file.size > maxBytes) {
    throw new Error("FILE_TOO_LARGE");
  }
}
